import { ChangeEvent, useRef } from 'react';
import { Button, ButtonProps, DateValue } from 'react-aria-components';
import { I18nProvider, useDateFormatter, useLocale } from '@react-aria/i18n';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps } from '@react-aria/utils';
import { CalendarState, useCalendarState } from '@react-stately/calendar';
import Dropdown from '@components/Dropdown';
import { useState } from 'react';
import {
  AriaCalendarCellProps,
  AriaCalendarGridProps,
  AriaCalendarProps,
  useCalendar,
  useCalendarCell,
  useCalendarGrid,
} from '@react-aria/calendar';
import {
  CalendarDate,
  createCalendar,
  getWeeksInMonth,
  today,
  isEqualDay,
} from '@internationalized/date';
import { FocusableElement, DOMAttributes } from '@react-types/shared';

import { ReactComponent as ArrowRight } from 'img/sort-up-duotone-solid.svg';
import './Calendar.scss';

interface MonthDropdownProps {
  state: CalendarState;
}

interface YearDropdownProps {
  state: CalendarState;
}


export default function Calendar(props: AriaCalendarProps<DateValue>) {
  const { locale } = useLocale();
  const state = useCalendarState({
    ...props,
    locale,
    createCalendar,
  });

  const { calendarProps, prevButtonProps, nextButtonProps } = useCalendar(
    props,
    state,
  );

  return (
    <I18nProvider locale="en-US">
      <div {...calendarProps} className="calendar-container">
        <CalendarHeader
          state={state}
          calendarProps={calendarProps}
          prevButtonProps={prevButtonProps}
          nextButtonProps={nextButtonProps}
        />
        <CalendarGrid state={state} />
      </div>
    </I18nProvider>
  );
}

function CalendarHeader({
  state,
  prevButtonProps,
}: {
  state: CalendarState;
  calendarProps: DOMAttributes<FocusableElement>;
  prevButtonProps: ButtonProps;
  nextButtonProps: ButtonProps;
}) {
  return (
    <div className="calendar-header">
      <Button {...prevButtonProps} className="calendar-button">
        <ArrowRight className="icon-rotate" />
      </Button>
      <div className="calendar-dropdowns">
        <MonthDropdown state={state} />
        <YearDropdown state={state} />
      </div>
    </div>
  );
}

export function MonthDropdown({ state }: MonthDropdownProps) {
  const formatter = useDateFormatter({ month: "long", timeZone: state.timeZone });

  const months = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: formatter.format(state.focusedDate.set({ month: i + 1 }).toDate(state.timeZone)),
  }));

  const [selectedMonth, setSelectedMonth] = useState(state.focusedDate.month.toString());

  const handleChange = (value: string) => {
    setSelectedMonth(value);
    state.setFocusedDate(state.focusedDate.set({ month: Number(value) }));
  };

  return (
    <Dropdown
      options={months}
      value={selectedMonth}
      onChange={handleChange}
      className='drop-month'
    />
  );
}
export function YearDropdown({ state }: YearDropdownProps) {
  const formatter = useDateFormatter({ year: "numeric", timeZone: state.timeZone });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 101 }, (_, i) => {
    const year = currentYear - i;
    return {
      value: String(year),
      label: formatter.format(state.focusedDate.set({ year }).toDate(state.timeZone)),
    };
  });

  const [selectedYear, setSelectedYear] = useState(state.focusedDate.year.toString());

  const handleChange = (value: string) => {
    setSelectedYear(value);
    state.setFocusedDate(state.focusedDate.set({ year: Number(value) }));
  };

  return (
    <Dropdown
      placeholder="연도 선택"
      options={years}
      value={selectedYear}
      onChange={handleChange}
      className='drop-year'
    />
  );
}

function CalendarGrid({ state, ...props }: AriaCalendarGridProps & { state: CalendarState }) {
  const { locale } = useLocale();
  const { gridProps, headerProps } = useCalendarGrid(props, state);

  const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);

  return (
    <table {...gridProps} className="calendar-grid">
      <thead {...headerProps}>
        <tr>
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day, index) => (
            <th key={index} className="calendar-day">
              {day}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
          <tr key={weekIndex}>
            {state.getDatesInWeek(weekIndex).map((date, i) =>
              date ? <CalendarCell key={i} state={state} date={date} /> : <td key={i} className="calendar-empty" />,
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CalendarCell({ state, ...props }: AriaCalendarCellProps & { state: CalendarState }) {
  const ref = useRef<HTMLDivElement>(null);
  const now = today(state.timeZone);

  const {
    cellProps,
    buttonProps,
    formattedDate,
    isSelected,
    isDisabled,
    isOutsideVisibleRange,
  } = useCalendarCell(props, state, ref);
  const { focusProps, isFocusVisible } = useFocusRing();

  const isToday = isEqualDay(props.date, now);

  return (
    <td {...cellProps} className="calendar-cell">
      <div
        ref={ref}
        hidden={isOutsideVisibleRange}
        {...mergeProps(buttonProps, focusProps)}
        className={`calendar-date ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isDisabled ? 'disabled' : ''}`}
      >
        {formattedDate}
      </div>
    </td>
  );
}
